import { useEffect, useMemo, useReducer, useRef } from 'react';
import { api } from '../api.ts';
import { createSocket, type AppSocket } from '../socket.ts';
import type {
  LeaderboardEntry,
  MeResponse,
  PublicRound,
  RevealPayload,
  UserStats,
} from '../../../shared/types.ts';

export type ConnectionState = 'connecting' | 'open' | 'closed';

interface State {
  connection: ConnectionState;
  me: MeResponse | null;
  round: PublicRound | null;
  reveal: RevealPayload | null;
  participantCount: number;
  answeredCount: number;
  lastSubmittedAnswerId: number | null;
  miniLeaderboard: LeaderboardEntry[];
  yourMini: LeaderboardEntry | null;
  toasts: Array<{ id: number; kind: 'good' | 'bad' | 'info'; text: string }>;
}

type Action =
  | { type: 'connection'; value: ConnectionState }
  | { type: 'me'; me: MeResponse }
  | { type: 'me:stats'; stats: UserStats }
  | { type: 'round:start'; round: PublicRound }
  | { type: 'round:tick'; participantCount: number; answeredCount: number; roundId: number }
  | { type: 'round:reveal'; reveal: RevealPayload }
  | { type: 'submit'; answerId: number }
  | { type: 'leaderboard:mini'; entries: LeaderboardEntry[]; yours: LeaderboardEntry | null }
  | { type: 'toast:add'; kind: 'good' | 'bad' | 'info'; text: string }
  | { type: 'toast:remove'; id: number };

let toastIdSeq = 1;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'connection':
      return { ...state, connection: action.value };
    case 'me':
      return { ...state, me: action.me };
    case 'me:stats':
      return state.me ? { ...state, me: { ...state.me, stats: action.stats } } : state;
    case 'round:start':
      return {
        ...state,
        round: action.round,
        reveal: action.round.phase === 'reveal' ? state.reveal : null,
        lastSubmittedAnswerId: null,
        participantCount: action.round.participantCount,
        answeredCount: action.round.answeredCount,
      };
    case 'round:tick':
      if (!state.round || state.round.roundId !== action.roundId) return state;
      return { ...state, participantCount: action.participantCount, answeredCount: action.answeredCount };
    case 'round:reveal':
      return { ...state, reveal: action.reveal };
    case 'submit':
      return {
        ...state,
        lastSubmittedAnswerId: action.answerId,
        // Server tallies distinct browsers, so re-picking the same round shouldn't bump the count.
        answeredCount:
          state.lastSubmittedAnswerId === null ? state.answeredCount + 1 : state.answeredCount,
      };
    case 'leaderboard:mini':
      return { ...state, miniLeaderboard: action.entries, yourMini: action.yours };
    case 'toast:add':
      return {
        ...state,
        toasts: [...state.toasts, { id: toastIdSeq++, kind: action.kind, text: action.text }].slice(-5),
      };
    case 'toast:remove':
      return { ...state, toasts: state.toasts.filter((t) => t.id !== action.id) };
    default:
      return state;
  }
}

const initial: State = {
  connection: 'connecting',
  me: null,
  round: null,
  reveal: null,
  participantCount: 0,
  answeredCount: 0,
  lastSubmittedAnswerId: null,
  miniLeaderboard: [],
  yourMini: null,
  toasts: [],
};

export interface GameStateApi {
  state: State;
  submitAnswer: (answerId: number) => void;
  reloadMe: () => Promise<void>;
}

export function useGameState(): GameStateApi {
  const [state, dispatch] = useReducer(reducer, initial);
  const socketRef = useRef<AppSocket | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    let cancelled = false;
    void api
      .me()
      .then((me) => {
        if (cancelled) return;
        dispatch({ type: 'me', me });
      })
      .catch(() => {
        // Will retry on reconnect.
      });

    const sock = createSocket();
    socketRef.current = sock;

    sock.on('connect', () => dispatch({ type: 'connection', value: 'open' }));
    sock.io.on('reconnect_attempt', () => dispatch({ type: 'connection', value: 'connecting' }));
    sock.on('disconnect', () => dispatch({ type: 'connection', value: 'closed' }));
    sock.on('connect_error', () => dispatch({ type: 'connection', value: 'closed' }));

    sock.on('round:start', (round) => {
      dispatch({ type: 'round:start', round });
    });
    sock.on('round:tick', (t) => dispatch({ type: 'round:tick', ...t }));
    sock.on('round:reveal', (rev) => {
      dispatch({ type: 'round:reveal', reveal: rev });
      if (rev.yourResult) {
        if (rev.yourResult.isCorrect) {
          const bonus = rev.yourResult.streakBonus;
          dispatch({
            type: 'toast:add',
            kind: 'good',
            text: bonus > 0
              ? `Correct! +${rev.yourResult.pointsAwarded} (streak bonus +${bonus})`
              : `Correct! +${rev.yourResult.pointsAwarded}`,
          });
        } else if (rev.yourResult.answerId === null) {
          // Skipped — no toast (intentional silence to avoid spam when the user steps away).
        } else {
          dispatch({ type: 'toast:add', kind: 'bad', text: 'Wrong. -1' });
        }
      }
    });
    sock.on('me:stats', (stats) => dispatch({ type: 'me:stats', stats }));
    sock.on('leaderboard:mini', (m) => dispatch({ type: 'leaderboard:mini', entries: m.entries, yours: m.yourEntry }));

    // Reconnection nudges.
    const onVisible = (): void => {
      if (!sock.connected) sock.connect();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    window.addEventListener('online', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.removeEventListener('online', onVisible);
      sock.disconnect();
      socketRef.current = null;
    };
  }, []);

  // Auto-dismiss toasts.
  useEffect(() => {
    if (state.toasts.length === 0) return;
    const last = state.toasts[state.toasts.length - 1]!;
    const timer = setTimeout(() => dispatch({ type: 'toast:remove', id: last.id }), 3500);
    return () => clearTimeout(timer);
  }, [state.toasts]);

  const api2 = useMemo<GameStateApi>(() => {
    return {
      state,
      submitAnswer(answerId) {
        const s = stateRef.current;
        if (!s.round) return;
        if (s.round.phase !== 'question') return;
        // No-op when the user taps the answer they already have selected.
        if (s.lastSubmittedAnswerId === answerId) return;
        const sock = socketRef.current;
        if (!sock) return;
        dispatch({ type: 'submit', answerId });
        sock.emit('answer:submit', { roundId: s.round.roundId, answerId }, () => {
          // Optimistic UI is fine here; benign races (rate limit, late round end) just
          // mean the server keeps whichever pick it accepted last.
        });
      },
      async reloadMe() {
        const me = await api.me();
        dispatch({ type: 'me', me });
      },
    };
  }, [state]);

  return api2;
}
